import { GLCode } from '@rygen/rygen-ui/model';

import { Shipment } from './Shipment';
import { ShipmentLineItem } from './ShipmentLineItem';
import { ShipmentStop } from './ShipmentStop';

type ConstructableManualShipment = Partial<Omit<ManualShipment, 'id'>> & {
  // These fields could be passed in from an order template
  origin?: ShipmentStop;
  destination?: ShipmentStop;
};

/**
 * A manually-entered shipment
 *
 * This class represents a request to create a shipment directly (without
 * specifying orders). Instances aren't persisted yet and always have undefined
 * `id`s.
 *
 * When this shipment is submitted, an order will be created for each unique
 * origin/destination pair.
 *
 * @see https://rygen.atlassian.net/browse/CS-7144
 */
export class ManualShipment extends Shipment {
  id = undefined;
  /** `GLCode`s that will be applied to each created order */
  glCodes: GLCode[] = [];
  skipAutoTender = false;

  readonly type = 'ManualShipment';

  constructor(opts?: ConstructableManualShipment) {
    super({
      ...opts,
      // If caller passed `stops`-> map them with `sequenceNumber`s
      // else -> create list with `origin`/`destination` or default stops
      stops: opts?.stops?.length
        ? opts.stops.map(
            (stop, index) =>
              new ShipmentStop({
                ...stop,
                sequenceNumber: index + 1,
              }),
          )
        : [
            opts?.origin
              ? new ShipmentStop(opts.origin)
              : Shipment.fallbackOrigin(),
            opts?.destination
              ? new ShipmentStop(opts.destination)
              : Shipment.fallbackDestination(),
          ],
    });

    if (!this.lineItems.length) {
      const lineItem = new ShipmentLineItem({
        pickupStopSequenceNumber: 1,
        dropOffStopSequenceNumber: 2,
      });
      this.lineItems = [lineItem];
    }

    if (opts?.glCodes) {
      this.glCodes = opts.glCodes.map((val) => new GLCode(val));
    }
    if (opts?.skipAutoTender != null) {
      this.skipAutoTender = opts.skipAutoTender;
    }
  }
}
